/**
 * Email Hub API Service
 * Handles all API calls for the admin Email Hub feature and public contact form.
 *
 * Uses shared API client with automatic token refresh for session extension.
 */

import { apiClient as api } from './apiClient';
import { API_BASE_URL } from '../config';

const EMAIL_HUB_API_BASE = `${API_BASE_URL}/api/v1/admin/email-hub`;
const CONTACT_API_BASE = `${API_BASE_URL}/api/v1/contact`;

// ---------------------------------------------------------------------------
// Enums / String unions
// ---------------------------------------------------------------------------

export type EmailStatus = 'new' | 'read' | 'replied' | 'auto_replied' | 'archived' | 'spam';

export type EmailPriority = 'low' | 'normal' | 'high' | 'urgent';

export type EmailIntent =
  | 'account_help'
  | 'billing_inquiry'
  | 'bug_report'
  | 'feature_request'
  | 'creator_inquiry'
  | 'partnership'
  | 'content_feedback'
  | 'general_question'
  | 'spam'
  | 'other';

export type ContactFormStatus = 'new' | 'read' | 'replied' | 'archived';

export type ContactFormCategory =
  | 'general'
  | 'partnership'
  | 'creator_inquiry'
  | 'bug_report'
  | 'feedback'
  | 'billing'
  | 'press';

// ---------------------------------------------------------------------------
// Core interfaces
// ---------------------------------------------------------------------------

export interface InboundEmail {
  id: string;
  resendEmailId?: string;
  fromAddress: string;
  fromName?: string;
  toAddresses: string[];
  subject: string;
  textBody?: string;
  htmlBody?: string;
  intent?: EmailIntent;
  intentConfidence?: number;
  intentMetadata?: Record<string, unknown>;
  status: EmailStatus;
  priority: EmailPriority;
  matchedUser?: {
    id: string;
    email: string;
    fullName?: string;
    username?: string;
  };
  replies?: Array<{ body: string; sentAt: string; resendEmailId?: string }>;
  repliedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContactFormSubmission {
  id: string;
  name: string;
  email: string;
  subject?: string;
  message: string;
  category?: ContactFormCategory;
  intent?: string;
  intentConfidence?: number;
  replies?: Array<{ body: string; sentAt: string; resendEmailId?: string }>;
  status: ContactFormStatus;
  matchedUser?: {
    id: string;
    email: string;
    fullName?: string;
    username?: string;
  };
  language?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmailStats {
  total: number;
  new: number;
  unread: number;
  spam: number;
  today: number;
  intentBreakdown: Partial<Record<EmailIntent, number>>;
  emails?: { total: number; new: number; unread: number; spam: number; today: number };
  contactForms?: { total: number; new: number; today: number };
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ---------------------------------------------------------------------------
// Request parameter interfaces
// ---------------------------------------------------------------------------

export interface GetEmailsParams {
  status?: EmailStatus;
  priority?: EmailPriority;
  intent?: EmailIntent;
  mailbox?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface GetContactFormsParams {
  status?: ContactFormStatus;
  category?: ContactFormCategory;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ReplyToEmailBody {
  body: string;
}

export interface SubmitContactFormData {
  name: string;
  email: string;
  subject?: string;
  message: string;
  category?: ContactFormCategory;
}

// ---------------------------------------------------------------------------
// Email Hub service
// ---------------------------------------------------------------------------

export const emailHubService = {
  // -------------------------------------------------------------------------
  // Admin inbox
  // -------------------------------------------------------------------------

  /**
   * Get paginated list of inbound emails with optional filters.
   */
  async getEmails(
    params?: GetEmailsParams,
  ): Promise<PaginatedResponse<InboundEmail>> {
    const response = await api.get<{
      success: boolean;
      data: PaginatedResponse<InboundEmail>;
    }>(EMAIL_HUB_API_BASE, { params });
    return response.data.data;
  },

  /**
   * Get a single inbound email by ID.
   */
  async getEmailById(id: string): Promise<InboundEmail> {
    const response = await api.get<{ success: boolean; data: InboundEmail }>(
      `${EMAIL_HUB_API_BASE}/${id}`,
    );
    return response.data.data;
  },

  /**
   * Get dashboard stats for the email hub.
   */
  async getStats(): Promise<EmailStats> {
    const response = await api.get<{ success: boolean; data: EmailStats }>(
      `${EMAIL_HUB_API_BASE}/stats`,
    );
    return response.data.data;
  },

  /**
   * Bulk-mark every NEW email + contact form as READ.
   * Returns counts of items updated for each pool.
   */
  async markAllAsRead(): Promise<{ emails: number; contactForms: number }> {
    const response = await api.post<{
      success: boolean;
      data: { emails: number; contactForms: number };
    }>(`${EMAIL_HUB_API_BASE}/mark-all-read`);
    return response.data.data;
  },

  /**
   * Update the status of an inbound email.
   */
  async updateEmailStatus(id: string, status: EmailStatus): Promise<InboundEmail> {
    const response = await api.patch<{ success: boolean; data: InboundEmail }>(
      `${EMAIL_HUB_API_BASE}/${id}/status`,
      { status },
    );
    return response.data.data;
  },

  /**
   * Update the priority of an inbound email.
   */
  async updateEmailPriority(
    id: string,
    priority: EmailPriority,
  ): Promise<InboundEmail> {
    const response = await api.patch<{ success: boolean; data: InboundEmail }>(
      `${EMAIL_HUB_API_BASE}/${id}/priority`,
      { priority },
    );
    return response.data.data;
  },

  /**
   * Delete an inbound email.
   */
  async deleteEmail(id: string): Promise<void> {
    await api.delete(`${EMAIL_HUB_API_BASE}/${id}`);
  },

  /**
   * Send a reply to an inbound email.
   */
  async replyToEmail(id: string, body: ReplyToEmailBody): Promise<InboundEmail> {
    const response = await api.post<{ success: boolean; data: InboundEmail }>(
      `${EMAIL_HUB_API_BASE}/${id}/reply`,
      body,
    );
    return response.data.data;
  },

  // -------------------------------------------------------------------------
  // Contact forms
  // -------------------------------------------------------------------------

  /**
   * Get paginated list of contact form submissions with optional filters.
   */
  async getContactForms(
    params?: GetContactFormsParams,
  ): Promise<PaginatedResponse<ContactFormSubmission>> {
    const response = await api.get<{
      success: boolean;
      data: PaginatedResponse<ContactFormSubmission>;
    }>(`${EMAIL_HUB_API_BASE}/contact-forms`, { params });
    return response.data.data;
  },

  /**
   * Reply to a contact form submission.
   */
  async replyToContactForm(id: string, body: ReplyToEmailBody): Promise<ContactFormSubmission> {
    const response = await api.post<{ success: boolean; data: ContactFormSubmission }>(
      `${EMAIL_HUB_API_BASE}/contact-forms/${id}/reply`,
      body,
    );
    return response.data.data;
  },

  /**
   * Delete a contact form submission.
   */
  async deleteContactForm(id: string): Promise<void> {
    await api.delete(`${EMAIL_HUB_API_BASE}/contact-forms/${id}`);
  },

  /**
   * Update the status of a contact form submission.
   */
  async updateContactFormStatus(
    id: string,
    status: ContactFormStatus,
  ): Promise<ContactFormSubmission> {
    const response = await api.patch<{
      success: boolean;
      data: ContactFormSubmission;
    }>(`${EMAIL_HUB_API_BASE}/contact-forms/${id}/status`, { status });
    return response.data.data;
  },

  // -------------------------------------------------------------------------
  // Public contact form
  // -------------------------------------------------------------------------

  /**
   * Submit a public contact form (no auth required).
   */
  async submitContactForm(
    data: SubmitContactFormData,
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      CONTACT_API_BASE,
      data,
    );
    return response.data;
  },
};
